(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-wi-global-footprint]';

  const BUILT_IN_COORDINATES = {
    dubai: { lat: 25.2048, lon: 55.2708 },
    bahrain: { lat: 26.0667, lon: 50.5577 },
    nepal: { lat: 28.3949, lon: 84.1240 },
    'sri lanka': { lat: 7.8731, lon: 80.7718 },
    bangladesh: { lat: 23.6850, lon: 90.3563 },
    canada: { lat: 56.1304, lon: -106.3468 },
    philippines: { lat: 12.8797, lon: 121.7740 },
    qatar: { lat: 25.3548, lon: 51.1839 }
  };

  const ROBINSON_X = [
    1.0000, 0.9986, 0.9954, 0.9900, 0.9822, 0.9730, 0.9600,
    0.9427, 0.9216, 0.8962, 0.8679, 0.8350, 0.7986, 0.7597,
    0.7186, 0.6732, 0.6213, 0.5722, 0.5322
  ];

  const ROBINSON_Y = [
    0.0000, 0.0620, 0.1240, 0.1860, 0.2480, 0.3100, 0.3720,
    0.4340, 0.4958, 0.5571, 0.6176, 0.6769, 0.7346, 0.7903,
    0.8435, 0.8936, 0.9394, 0.9761, 1.0000
  ];

  // The current wi-gf-world-map.svg uses the Robinson projection
  // centred at 11°E.
  const MAP_CENTRAL_MERIDIAN = 11;

  const clamp = (value, min, max) =>
    Math.min(max, Math.max(min, value));

  const normalizeLocation = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');

  const parseCoordinate = (value, min, max) => {
    if (
      value === undefined ||
      value === null ||
      String(value).trim() === ''
    ) {
      return null;
    }

    const parsed = Number.parseFloat(value);

    return Number.isFinite(parsed) && parsed >= min && parsed <= max
      ? parsed
      : null;
  };

  const interpolateRobinson = (table, latitude) => {
    const absoluteLatitude = clamp(Math.abs(latitude), 0, 90);
    const lowerIndex = Math.min(
      Math.floor(absoluteLatitude / 5),
      table.length - 2
    );
    const fraction =
      (absoluteLatitude - lowerIndex * 5) / 5;

    return (
      table[lowerIndex] +
      (table[lowerIndex + 1] - table[lowerIndex]) * fraction
    );
  };

  const projectRobinson = (latitude, longitude) => {
    const xCoefficient = interpolateRobinson(
      ROBINSON_X,
      latitude
    );
    const yCoefficient = interpolateRobinson(
      ROBINSON_Y,
      latitude
    );

    let relativeLongitude =
      longitude - MAP_CENTRAL_MERIDIAN;

    relativeLongitude =
      ((relativeLongitude + 180) % 360 + 360) % 360 - 180;

    const projectedX =
      relativeLongitude * xCoefficient;

    const projectedY =
      latitude === 0
        ? 0
        : -Math.sign(latitude) * 91.296 * yCoefficient;

    return {
      x: clamp(
        ((projectedX + 180) / 360) * 100,
        0,
        100
      ),
      y: clamp(
        ((projectedY + 91.296) / 182.592) * 100,
        0,
        100
      )
    };
  };

  class GlobalFootprintSection {
    constructor(section) {
      this.section = section;

      this.map = section.querySelector(
        '[data-wi-gf-map]'
      );
      this.zoomLayer = section.querySelector(
        '[data-wi-gf-zoom-layer]'
      );
      this.resetButton = section.querySelector(
        '[data-wi-gf-reset]'
      );
      this.panel = section.querySelector(
        '[data-wi-gf-panel]'
      );

      this.markers = Array.from(
        section.querySelectorAll('[data-wi-gf-marker]')
      );

      this.details = Array.from(
        section.querySelectorAll('[data-wi-gf-location]')
      );

      this.handleClick = this.handleClick.bind(this);
      this.handleToggle = this.handleToggle.bind(this);

      this.positionMarkers();
      this.bind();
      this.syncInitialState();
    }

    bind() {
      // All listeners are attached ONLY to this section.
      // No Shopify editor lifecycle events are used.
      this.section.addEventListener(
        'click',
        this.handleClick
      );

      // Native <details> handles expansion by itself.
      // We only observe toggles to sync the map.
      this.section.addEventListener(
        'toggle',
        this.handleToggle,
        true
      );
    }

    resolveCoordinates(marker) {
      const latitude = parseCoordinate(
        marker.dataset.latitude,
        -90,
        90
      );

      const longitude = parseCoordinate(
        marker.dataset.longitude,
        -180,
        180
      );

      if (latitude !== null && longitude !== null) {
        return {
          lat: latitude,
          lon: longitude
        };
      }

      return (
        BUILT_IN_COORDINATES[
          normalizeLocation(marker.dataset.location)
        ] || null
      );
    }

    positionMarkers() {
      this.markers.forEach((marker) => {
        const coordinates =
          this.resolveCoordinates(marker);

        let point;

        if (coordinates) {
          point = projectRobinson(
            coordinates.lat,
            coordinates.lon
          );
        } else {
          point = {
            x: clamp(
              Number.parseFloat(
                marker.dataset.fallbackX || '50'
              ),
              0,
              100
            ),
            y: clamp(
              Number.parseFloat(
                marker.dataset.fallbackY || '50'
              ),
              0,
              100
            )
          };
        }

        marker.dataset.mapX =
          point.x.toFixed(4);

        marker.dataset.mapY =
          point.y.toFixed(4);

        marker.style.setProperty(
          '--wi-gf-x',
          `${point.x.toFixed(4)}%`
        );

        marker.style.setProperty(
          '--wi-gf-y',
          `${point.y.toFixed(4)}%`
        );

        marker.classList.add('is-positioned');
      });
    }

    handleClick(event) {
      const resetButton = event.target.closest(
        '[data-wi-gf-reset]'
      );

      if (
        resetButton &&
        this.section.contains(resetButton)
      ) {
        event.preventDefault();
        this.closeAll();
        return;
      }

      const marker = event.target.closest(
        '[data-wi-gf-marker]'
      );

      if (
        !marker ||
        !this.section.contains(marker)
      ) {
        return;
      }

      event.preventDefault();

      const detail = this.findDetail(
        marker.dataset.blockId
      );

      if (!detail) return;

      if (detail.open) {
        detail.open = false;
        return;
      }

      this.closeOtherDetails(detail);
      detail.open = true;

      // Do not use document scrolling in the Theme Editor.
      // On the live storefront, only the internal locations
      // panel may scroll if the opened item is outside view.
      this.revealInsidePanel(detail);
    }

    handleToggle(event) {
      const detail = event.target;

      if (
        !(detail instanceof HTMLDetailsElement) ||
        !detail.matches('[data-wi-gf-location]')
      ) {
        return;
      }

      if (detail.open) {
        this.closeOtherDetails(detail);
        this.activateLocation(
          detail.dataset.blockId
        );
      } else if (
        !this.details.some((item) => item.open)
      ) {
        this.resetMap();
      }
    }

    findDetail(blockId) {
      return this.details.find(
        (detail) =>
          detail.dataset.blockId === blockId
      );
    }

    findMarker(blockId) {
      return this.markers.find(
        (marker) =>
          marker.dataset.blockId === blockId
      );
    }

    closeOtherDetails(activeDetail) {
      this.details.forEach((detail) => {
        if (
          detail !== activeDetail &&
          detail.open
        ) {
          detail.open = false;
        }
      });
    }

    activateLocation(blockId) {
      const marker = this.findMarker(blockId);

      if (!marker) return;

      const x = Number.parseFloat(
        marker.dataset.mapX || '50'
      );

      const y = Number.parseFloat(
        marker.dataset.mapY || '50'
      );

      if (this.zoomLayer) {
        this.zoomLayer.style.setProperty(
          '--wi-gf-origin-x',
          `${x}%`
        );

        this.zoomLayer.style.setProperty(
          '--wi-gf-origin-y',
          `${y}%`
        );
      }

      this.section.classList.add('is-zoomed');

      this.markers.forEach((item) => {
        item.classList.toggle(
          'is-active',
          item === marker
        );
      });

      if (this.resetButton) {
        this.resetButton.hidden = false;
      }
    }

    resetMap() {
      this.section.classList.remove('is-zoomed');

      if (this.zoomLayer) {
        this.zoomLayer.style.setProperty(
          '--wi-gf-origin-x',
          '50%'
        );

        this.zoomLayer.style.setProperty(
          '--wi-gf-origin-y',
          '50%'
        );
      }

      this.markers.forEach((marker) => {
        marker.classList.remove('is-active');
      });

      if (this.resetButton) {
        this.resetButton.hidden = true;
      }
    }

    closeAll() {
      this.details.forEach((detail) => {
        detail.open = false;
      });

      this.resetMap();
    }

    syncInitialState() {
      const openDetail = this.details.find(
        (detail) => detail.open
      );

      if (openDetail) {
        this.activateLocation(
          openDetail.dataset.blockId
        );
      } else {
        this.resetMap();
      }
    }

    revealInsidePanel(detail) {
      // Shopify Theme Editor stability takes priority.
      if (
        window.Shopify &&
        window.Shopify.designMode
      ) {
        return;
      }

      if (!this.panel || !detail) return;

      const panelRect =
        this.panel.getBoundingClientRect();

      const detailRect =
        detail.getBoundingClientRect();

      const margin = 8;
      let delta = 0;

      if (
        detailRect.top <
        panelRect.top + margin
      ) {
        delta =
          detailRect.top -
          panelRect.top -
          margin;
      } else if (
        detailRect.bottom >
        panelRect.bottom - margin
      ) {
        delta =
          detailRect.bottom -
          panelRect.bottom +
          margin;
      }

      if (!delta) return;

      const reducedMotion =
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

      this.panel.scrollBy({
        top: delta,
        behavior: reducedMotion
          ? 'auto'
          : 'smooth'
      });
    }
  }

  const initialize = (root = document) => {
    const sections = [];

    if (
      root instanceof Element &&
      root.matches(SECTION_SELECTOR)
    ) {
      sections.push(root);
    }

    if (root.querySelectorAll) {
      root
        .querySelectorAll(SECTION_SELECTOR)
        .forEach((section) => {
          sections.push(section);
        });
    }

    sections.forEach((section) => {
      // Prevent duplicate listeners if this same DOM node
      // encounters the script more than once.
      if (
        section.dataset.wiGfJsInitialized ===
        'true'
      ) {
        return;
      }

      section.dataset.wiGfJsInitialized =
        'true';

      try {
        new GlobalFootprintSection(section);
      } catch (error) {
        // Never throw into Shopify Theme Editor.
        // Native accordions still work without JS enhancement.
        console.error(
          '[WI Global Footprint] initialization failed:',
          error
        );
      }
    });
  };

  // No Shopify section/block lifecycle listeners are registered.
  // The script initializes whatever Global Footprint section
  // exists when this asset is executed.
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize(document),
      { once: true }
    );
  } else {
    initialize(document);
  }
})();